import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PKCEUtil } from '../utils/pkce.util';
import { SessionService } from './session.service';

export interface KeycloakTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  refresh_expires_in: number;
  token_type: string;
  id_token?: string;
  scope?: string;
}

@Injectable({ providedIn: 'root' })
export class KeycloakAuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly sessionService = inject(SessionService);

  // Storage keys
  private readonly CODE_VERIFIER_KEY = 'keycloak_code_verifier';
  private readonly STATE_KEY = 'keycloak_state';
  private readonly RETURN_URL_KEY = 'keycloak_return_url';

  // Observable for authentication state
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  constructor() {
    // Initialization handled by init() via APP_INITIALIZER
  }

  /**
   * Initialize auth state by checking session
   */
  async init(): Promise<void> {
    const isAuthenticated = this.sessionService.isAuthenticated();
    this.isAuthenticatedSubject.next(isAuthenticated);
  }

  /**
   * Check if user is authenticated via session
   */
  checkAuth(): Observable<boolean> {
    const isAuthenticated = this.sessionService.isAuthenticated();
    this.isAuthenticatedSubject.next(isAuthenticated);
    return of(isAuthenticated);
  }

  /**
   * Generate PKCE code_verifier and code_challenge
   */
  async generatePKCE(): Promise<{ codeVerifier: string; codeChallenge: string }> {
    const codeVerifier = PKCEUtil.generateCodeVerifier();
    const codeChallenge = await PKCEUtil.generateCodeChallenge(codeVerifier);
    return { codeVerifier, codeChallenge };
  }

  /**
   * Redirect user to Keycloak login page with PKCE parameters
   */
  async redirectToKeycloakLogin(returnUrl: string = '/'): Promise<void> {
    try {
      // Check if user already has a valid session
      if (this.sessionService.isAuthenticated()) {
        // Already authenticated, just redirect
        this.router.navigate([returnUrl]);
        return;
      }

      const { codeVerifier, codeChallenge } = await this.generatePKCE();
      const state = PKCEUtil.generateRandomString(32);

      sessionStorage.setItem(this.CODE_VERIFIER_KEY, codeVerifier);
      sessionStorage.setItem(this.STATE_KEY, state);
      sessionStorage.setItem(this.RETURN_URL_KEY, returnUrl);

      const params = new URLSearchParams({
        response_type: 'code',
        client_id: environment.keycloak.clientId,
        redirect_uri: this.getRedirectUri(),
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
        state: state,
        scope: 'openid profile'
      });

      const authUrl = `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/auth?${params}`;
      window.location.href = authUrl;
    } catch (error) {
      console.error('Error initiating Keycloak login:', error);
      throw error;
    }
  }

  /**
   * Exchange authorization code for session via backend
   */
  async exchangeCodeForToken(code: string, state?: string): Promise<void> {
    const storedState = sessionStorage.getItem(this.STATE_KEY);
    if (state && storedState && state !== storedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }

    const codeVerifier = sessionStorage.getItem(this.CODE_VERIFIER_KEY);
    if (!codeVerifier) {
      throw new Error('No code verifier found - PKCE flow incomplete');
    }

    try {
      // Exchange code for session via backend (new session management)
      await firstValueFrom(
        this.http.post('/api/v2/auth/callback', {
          code,
          codeVerifier,
          redirectUri: this.getRedirectUri()
        })
      );

      sessionStorage.removeItem(this.CODE_VERIFIER_KEY);
      sessionStorage.removeItem(this.STATE_KEY);

      this.isAuthenticatedSubject.next(true);
    } catch (error) {
      console.error('Error exchanging code:', error);
      this.clearTokens();
      throw error;
    }
  }

  private clearTokens(): void {
    try {
      sessionStorage.removeItem(this.CODE_VERIFIER_KEY);
      sessionStorage.removeItem(this.STATE_KEY);
      sessionStorage.removeItem(this.RETURN_URL_KEY);
    } catch (error) {
      console.warn('Error clearing tokens:', error);
    }
    this.isAuthenticatedSubject.next(false);
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  getReturnUrl(): string {
    return sessionStorage.getItem(this.RETURN_URL_KEY) || '/';
  }

  clearReturnUrl(): void {
    sessionStorage.removeItem(this.RETURN_URL_KEY);
  }

  async logout(): Promise<void> {
    const idToken = this.sessionService.getIdToken();
    await this.sessionService.logout();
    this.clearTokens();
    const logoutUrl = `${environment.keycloak.url}/realms/${environment.keycloak.realm}/protocol/openid-connect/logout?post_logout_redirect_uri=${encodeURIComponent(window.location.origin)}${idToken ? `&id_token_hint=${encodeURIComponent(idToken)}` : ''}`;
    window.location.href = logoutUrl;
  }

  async logoutLocal(): Promise<void> {
    await this.sessionService.logout();
    this.clearTokens();
    this.router.navigate(['/']);
  }

  private getRedirectUri(): string {
    return `${window.location.origin}/callback`;
  }
}
